// MARK://dhk8/admin-shell/pages/modules/CombosPage
// Lista de combos del catálogo → dhk8-catalog-combos.
// Presentación DS: DataTable + FilterBar + SummaryStrip + StatusPill.
// CTA "Nuevo combo" abre Sheet con name + priceCents (description opcional).
// Filtros: buscador (q) + activos/inactivos. Los stubs (combos sin items o
// nombres conocidos como "molletes con queso"/"torta") muestran un StatusPill
// "Necesita configuración" para que el comerciante los complete. Borrar con
// ConfirmDialog. Fix: crear combo refresca la lista antes de abrir la ficha.
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Package, Trash2 } from 'lucide-react'
import * as combos from '../../lib/clients/catalog-combos.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import Sheet from '../../components/Sheet.js'
import MoneyInput from '../../components/MoneyInput.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import { SummaryStrip } from '../../components/StatCard.js'
import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'

function mxn(cents: number): string {
  return (cents / 100).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
}

type ActiveFilter = 'all' | 'active' | 'inactive'

interface CreateForm { name: string; description: string; priceMxn: number; pricingMode: combos.ComboPricingMode; validFrom: string; validUntil: string }
const emptyForm = (): CreateForm => ({ name: '', description: '', priceMxn: 0, pricingMode: 'FIXED_PRICE', validFrom: '', validUntil: '' })

export default function CombosPage() {
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { toast } = useToast()
  const list = useAsync((s) => combos.listCombos({}, s))
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState<ActiveFilter>('all')
  const [sort, setSort] = useState<SortState>({ key: 'name', dir: 'asc' })

  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<CreateForm>(emptyForm())
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [busy, setBusy] = useState(false)

  const rows = useMemo(() => {
    const items = list.data ?? []
    const term = q.trim().toLowerCase()
    const filtered = items.filter((c) => {
      if (term && !c.name.toLowerCase().includes(term)) return false
      if (filter === 'active' && !c.active) return false
      if (filter === 'inactive' && c.active) return false
      return true
    })
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      switch (sort.key) {
        case 'price': return (a.priceCents - b.priceCents) * dir
        case 'items': return (a.items.length - b.items.length) * dir
        case 'status': return (Number(a.active) - Number(b.active)) * dir
        default: return a.name.localeCompare(b.name, 'es') * dir
      }
    })
  }, [list.data, q, filter, sort])

  const stats = useMemo(() => {
    const items = list.data ?? []
    const active = items.filter((c) => c.active).length
    const stubs = items.filter((c) => combos.isStub(c)).length
    return { total: items.length, active, stubs }
  }, [list.data])

  function openCreate() { setForm(emptyForm()); setFormError(null); setCreating(true) }

  async function saveCreate() {
    setFormError(null)
    if (!form.name.trim()) { setFormError('El nombre es obligatorio.'); return }
    if (form.priceMxn < 0) { setFormError('El precio no puede ser negativo.'); return }
    setSaving(true)
    try {
      const created = await combos.createCombo({
        name: form.name.trim(),
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
        priceCents: Math.round(form.priceMxn * 100),
        pricingMode: form.pricingMode,
        ...(form.validFrom ? { validFrom: new Date(form.validFrom).toISOString() } : {}),
        ...(form.validUntil ? { validUntil: new Date(form.validUntil).toISOString() } : {}),
      })
      setCreating(false)
      // Fix: refresca la lista antes de navegar para que, al volver, esté al día.
      list.reload()
      navigate(`/combos/${created.id}`)
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'No se pudo crear el combo.')
    } finally { setSaving(false) }
  }

  async function remove(c: combos.Combo) {
    const ok = await confirm({
      title: '¿Eliminar combo?',
      message: `Se eliminará “${c.name}”. Se puede revertir desde el backend (soft delete).`,
      tone: 'danger',
      confirmLabel: 'Eliminar',
    })
    if (!ok) return
    setBusy(true)
    try { await combos.deleteCombo(c.id); list.reload(); toast('Combo eliminado.', 'success') }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar.', 'danger') }
    finally { setBusy(false) }
  }

  const columns: DataTableColumn<combos.Combo>[] = [
    {
      key: 'name', header: 'Combo', sortable: true,
      render: (c) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Package size={14} aria-hidden style={{ opacity: 0.6, flexShrink: 0 }} />
          <span style={{ fontWeight: 500 }}>{c.name}</span>
          {c.description ? <span className="adm-muted" style={{ fontWeight: 400 }}>· {c.description}</span> : null}
        </span>
      ),
    },
    {
      key: 'status', header: 'Estado',
      render: (c) => (
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
          <StatusPill tone={lifecycleTone(c.active)} label={c.active ? 'Activo' : 'Inactivo'} />
          {combos.isStub(c) && <StatusPill tone="warn" label="Necesita configuración" />}
        </span>
      ),
    },
    {
      key: 'items', header: 'Productos', align: 'right', sortable: true,
      render: (c) => <span className="adm-mono">{c.items.length}</span>,
    },
    {
      key: 'price', header: 'Precio', align: 'right', sortable: true,
      render: (c) => <span className="adm-mono">{mxn(c.priceCents)}</span>,
    },
    {
      key: 'actions', header: '', align: 'right', width: '64px',
      render: (c) => (
        <span style={{ display: 'inline-flex', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" disabled={busy} onClick={() => remove(c)} aria-label="Eliminar" title="Eliminar"><Trash2 size={15} /></button>
        </span>
      ),
    },
  ]

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Combos</h1>
          <p className="adm-page__sub">Paquetes con varios productos a un precio fijo</p>
        </div>
        <div className="adm-page__actions">
          <button className="adm-btn adm-btn--primary" onClick={openCreate}>
            <Plus size={16} /> Nuevo combo
          </button>
        </div>
      </div>

      {list.data && (
        <div style={{ marginBottom: 16 }}>
          <SummaryStrip items={[
            { label: 'Combos', value: stats.total },
            { label: 'Activos', value: stats.active, tone: 'ok' },
            { label: 'Por configurar', value: stats.stubs, tone: stats.stubs > 0 ? 'warn' : 'neutral' },
          ]} />
        </div>
      )}

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && (
        <DataTable<combos.Combo>
          columns={columns}
          rows={rows}
          getRowKey={(c) => c.id}
          zebra
          sort={sort}
          onSortChange={setSort}
          onRowClick={(c) => navigate(`/combos/${c.id}`)}
          emptyText={list.data.length === 0 ? 'Aún no hay combos. Crea el primero para empezar.' : 'Ningún combo coincide con el filtro.'}
          toolbar={
            <FilterBar
              search={q}
              onSearch={setQ}
              searchPlaceholder="Buscar combo…"
              filters={[{
                key: 'active', label: 'Estado', value: filter,
                onChange: (v) => setFilter(v as ActiveFilter),
                options: [
                  { value: 'all', label: 'Todos' },
                  { value: 'active', label: 'Activos' },
                  { value: 'inactive', label: 'Inactivos' },
                ],
              }]}
              actions={<button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openCreate}><Plus size={15} /> Nuevo combo</button>}
            />
          }
        />
      )}

      <Sheet
        open={creating}
        title="Nuevo combo"
        onClose={() => { setCreating(false); setFormError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setCreating(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveCreate} disabled={saving || !form.name.trim()}>
            {saving ? 'Guardando…' : 'Crear combo'}
          </button>
        </>}
      >
        {formError && <div className="adm-banner adm-banner--danger" role="alert">{formError}</div>}
        <label className="adm-field">
          <span className="adm-field__label">Nombre</span>
          <input
            className="adm-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ej: Desayuno completo"
          />
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Descripción (opcional)</span>
          <input
            className="adm-input"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Café + concha + jugo"
          />
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Modo de precio</span>
          <select className="adm-select" value={form.pricingMode} onChange={(e) => setForm({ ...form, pricingMode: e.target.value as combos.ComboPricingMode })}>
            <option value="FIXED_PRICE">Precio fijo</option>
            <option value="SUM_WITH_DISCOUNT">Suma con descuento</option>
          </select>
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Precio (MXN)</span>
          <MoneyInput
            valueCents={Math.round(form.priceMxn * 100)}
            onChangeCents={(c) => setForm({ ...form, priceMxn: c / 100 })}
          />
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <label className="adm-field" style={{ flex: 1 }}>
            <span className="adm-field__label">Válido desde (opcional)</span>
            <input className="adm-input" type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} />
          </label>
          <label className="adm-field" style={{ flex: 1 }}>
            <span className="adm-field__label">Válido hasta (opcional)</span>
            <input className="adm-input" type="date" value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} />
          </label>
        </div>
        <p className="adm-row__meta" style={{ marginTop: 8 }}>
          Después de crearlo, agrégale productos en la página de detalle.
        </p>
      </Sheet>
    </div>
  )
}
